import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
    getAllReleaseNotes,
    getReleaseNotesState,
    markNoteAsRead,
} from '@/services/releaseNotes'
import { ReleaseNote } from '@/types/releaseNotes'
import { formatDateWithFormatter } from '@/utils/function'
import {
    FileText,
    Sparkles,
    Bug,
    Wrench,
    Shield,
    ChevronDown,
    ChevronUp,
    CheckCircle2,
    Circle,
    Calendar,
    Filter,
    Plus,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import ReleaseNotesImageUploader from '../ImageUploader'

type CategoryFilter = 'all' | ReleaseNote['category']

const ReleaseNotesList = () => {
    const { t } = useTranslation('release-notes')
    const navigate = useNavigate()
    const [releaseNotes, setReleaseNotes] = useState<ReleaseNote[]>([])
    const [readNotes, setReadNotes] = useState<string[]>([])
    const [expandedNote, setExpandedNote] = useState<string | null>(null)
    const [filter, setFilter] = useState<'all' | 'unread'>('all')
    const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all')

    useEffect(() => {
        const notes = getAllReleaseNotes()
        const state = getReleaseNotesState()
        setReleaseNotes(notes)
        setReadNotes(state.readNotes)
    }, [])

    const handleReadNote = (noteId: string) => {
        if (!readNotes.includes(noteId)) {
            markNoteAsRead(noteId)
            setReadNotes((prev) => [...prev, noteId])
        }
        setExpandedNote(expandedNote === noteId ? null : noteId)
    }

    const getCategoryIcon = (category: ReleaseNote['category']) => {
        const iconClass = 'h-5 w-5'
        switch (category) {
            case 'feature':
                return <Sparkles className={`${iconClass} text-emerald-500`} />
            case 'bugfix':
                return <Bug className={`${iconClass} text-red-500`} />
            case 'improvement':
                return <Wrench className={`${iconClass} text-blue-500`} />
            case 'security':
                return <Shield className={`${iconClass} text-orange-500`} />
            default:
                return <FileText className={`${iconClass} text-gray-500`} />
        }
    }

    const getCategoryBadgeClass = (category: ReleaseNote['category']) => {
        const variants: Record<string, string> = {
            feature: 'bg-emerald-100 text-emerald-700 border-emerald-200',
            bugfix: 'bg-red-100 text-red-700 border-red-200',
            improvement: 'bg-blue-100 text-blue-700 border-blue-200',
            security: 'bg-orange-100 text-orange-700 border-orange-200',
        }
        return variants[category] || 'bg-gray-100 text-gray-700 border-gray-200'
    }

    const filteredNotes = useMemo(() => {
        let notes = releaseNotes

        if (filter === 'unread') {
            notes = notes.filter((note) => !readNotes.includes(note.id))
        }

        if (categoryFilter !== 'all') {
            notes = notes.filter((note) => note.category === categoryFilter)
        }

        return notes
    }, [releaseNotes, readNotes, filter, categoryFilter])

    const stats = useMemo(() => {
        return {
            total: releaseNotes.length,
            unread: releaseNotes.length - readNotes.length,
            feature: releaseNotes.filter((n) => n.category === 'feature')
                .length,
            bugfix: releaseNotes.filter((n) => n.category === 'bugfix').length,
            improvement: releaseNotes.filter(
                (n) => n.category === 'improvement'
            ).length,
            security: releaseNotes.filter((n) => n.category === 'security')
                .length,
        }
    }, [releaseNotes, readNotes])

    return (
        <div className="space-y-2">
            {/* Header */}
            <div className="bg-white rounded-md border p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                            <FileText className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                            <h1 className="text-lg font-semibold">
                                {t('title')}
                            </h1>
                            <p className="text-sm text-muted-foreground">
                                {t('description')}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="gap-1">
                            <Circle className="h-3 w-3 fill-primary text-primary" />
                            {stats.unread} {t('unread')}
                        </Badge>
                        <Badge variant="outline">
                            {stats.total} {t('total')}
                        </Badge>
                        <Button
                            size="sm"
                            onClick={() => navigate('/release-notes/create')}
                            className="gap-1"
                        >
                            <Plus className="h-4 w-4" />
                            {t('create-new')}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Image Uploader for Devs */}
            <ReleaseNotesImageUploader />

            {/* Filters */}
            <div className="bg-white rounded-lg border p-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <Tabs
                        value={filter}
                        onValueChange={(v) => setFilter(v as 'all' | 'unread')}
                    >
                        <TabsList className="h-9">
                            <TabsTrigger value="all" className="text-xs px-3">
                                {t('all')}
                                <Badge
                                    variant="secondary"
                                    className="ml-1.5 h-5 px-1.5 text-[10px]"
                                >
                                    {stats.total}
                                </Badge>
                            </TabsTrigger>
                            <TabsTrigger
                                value="unread"
                                className="text-xs px-3"
                            >
                                {t('unread')}
                                {stats.unread > 0 && (
                                    <Badge className="ml-1.5 h-5 px-1.5 text-[10px] bg-primary">
                                        {stats.unread}
                                    </Badge>
                                )}
                            </TabsTrigger>
                        </TabsList>
                    </Tabs>

                    <div className="flex items-center gap-2">
                        <Filter className="h-4 w-4 text-muted-foreground" />
                        <div className="flex items-center gap-1 flex-wrap">
                            {(
                                [
                                    'all',
                                    'feature',
                                    'improvement',
                                    'bugfix',
                                    'security',
                                ] as const
                            ).map((cat) => (
                                <Button
                                    key={cat}
                                    variant={
                                        categoryFilter === cat
                                            ? 'default'
                                            : 'outline'
                                    }
                                    size="sm"
                                    className="h-7 text-xs px-2"
                                    onClick={() => setCategoryFilter(cat)}
                                >
                                    {cat === 'all' ? (
                                        t('all')
                                    ) : (
                                        <span className="flex items-center gap-1">
                                            {getCategoryIcon(cat)}
                                            {t(`category.${cat}`)}
                                        </span>
                                    )}
                                </Button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Release Notes List */}
            <div className="space-y-2">
                {filteredNotes.map((note) => {
                    const isRead = readNotes.includes(note.id)
                    const isExpanded = expandedNote === note.id

                    return (
                        <Card
                            key={`${note.id}-${note.date}`}
                            className={`transition-all duration-200 ${
                                !isRead
                                    ? 'border-l-4 border-l-primary bg-primary/[0.02]'
                                    : ''
                            } ${isExpanded ? 'shadow-md' : 'hover:shadow-sm'}`}
                        >
                            <CardHeader className="p-4 pb-3">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex items-start gap-3 flex-1">
                                        <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                                            {getCategoryIcon(note.category)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                <span className="font-bold text-primary">
                                                    v{note.version}
                                                </span>
                                                <Badge
                                                    variant="outline"
                                                    className={`text-xs ${getCategoryBadgeClass(note.category)}`}
                                                >
                                                    {t(
                                                        `category.${note.category}`
                                                    )}
                                                </Badge>
                                                {isRead ? (
                                                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                                                ) : (
                                                    <Circle className="h-4 w-4 fill-primary text-primary" />
                                                )}
                                            </div>
                                            <h3 className="font-semibold text-foreground mb-1">
                                                {note.title}
                                            </h3>
                                            <p className="text-sm text-muted-foreground line-clamp-2">
                                                {note.summary}
                                            </p>
                                            <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                                                <Calendar className="h-3.5 w-3.5" />
                                                {formatDateWithFormatter(
                                                    note.date,
                                                    'DD/MM/YYYY HH:mm'
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <Button
                                        variant={
                                            isExpanded ? 'default' : 'outline'
                                        }
                                        size="sm"
                                        className="shrink-0 gap-1"
                                        onClick={() => handleReadNote(note.id)}
                                    >
                                        {isExpanded ? (
                                            <>
                                                <ChevronUp className="h-4 w-4" />
                                                {t('collapse')}
                                            </>
                                        ) : (
                                            <>
                                                <ChevronDown className="h-4 w-4" />
                                                {t('read-more')}
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </CardHeader>

                            {isExpanded && (
                                <CardContent className="pt-0 px-4 pb-4">
                                    <div className="border-t pt-4">
                                        <ScrollArea className="max-h-[500px]">
                                            <div
                                                className="text-sm text-muted-foreground leading-relaxed"
                                                dangerouslySetInnerHTML={{
                                                    __html: note.content,
                                                }}
                                            />
                                        </ScrollArea>
                                    </div>
                                </CardContent>
                            )}
                        </Card>
                    )
                })}
            </div>

            {/* Empty State */}
            {filteredNotes.length === 0 && (
                <Card className="p-12">
                    <div className="text-center">
                        <div className="h-16 w-16 mx-auto rounded-full bg-muted flex items-center justify-center mb-4">
                            <FileText className="h-8 w-8 text-muted-foreground" />
                        </div>
                        <h3 className="font-semibold text-lg mb-1">
                            {filter === 'unread'
                                ? t('empty.no-unread')
                                : t('empty.title')}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                            {filter === 'unread'
                                ? t('empty.all-read')
                                : t('empty.description')}
                        </p>
                        {filter === 'unread' && (
                            <Button
                                variant="outline"
                                className="mt-4"
                                onClick={() => setFilter('all')}
                            >
                                {t('view-all')}
                            </Button>
                        )}
                    </div>
                </Card>
            )}
        </div>
    )
}

export default ReleaseNotesList
